import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, Plus, Trash2, Save } from "lucide-react";
const EXPLOITATION_STATUSES = ["Included", "Not Included"] as const;
import { useToast } from "@/hooks/use-toast";
import { clientExploitationService, type ClientExploitation as ExploitationType } from "@/services/clientExploitationService";
import { cn } from "@/lib/utils";

interface ClientExploitationProps {
  clientId: string;
}

interface EditableRow extends ExploitationType {
  isNew?: boolean;
  isDirty?: boolean;
}

export default function ClientExploitation({ clientId }: ClientExploitationProps) {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [editableRows, setEditableRows] = useState<EditableRow[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Generate year options (current year + 5 previous years + 2 future years)
  const yearOptions = Array.from({ length: 8 }, (_, i) => currentYear + 2 - i);

  // Fetch exploitations for the selected year
  const { data: exploitations = [], isLoading } = useQuery({
    queryKey: ["client-exploitations", clientId, selectedYear],
    queryFn: () => clientExploitationService.getExploitations(clientId, selectedYear),
    enabled: !!clientId,
  });

  // Sync editableRows with fetched data
  useEffect(() => {
    setEditableRows(exploitations.map(e => ({ ...e, isNew: false, isDirty: false })));
  }, [exploitations]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: clientExploitationService.createExploitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-exploitations", clientId, selectedYear] });
      toast({ title: "Success", description: "Row saved successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: clientExploitationService.updateExploitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-exploitations", clientId, selectedYear] });
      toast({ title: "Success", description: "Changes saved" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: clientExploitationService.deleteExploitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-exploitations", clientId, selectedYear] });
      toast({ title: "Success", description: "Row deleted" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Add new row
  const handleAddRow = useCallback(() => {
    const newRow: EditableRow = {
      id: `temp-${Date.now()}`,
      client_id: clientId,
      description: "",
      product_category: null,
      month: null,
      sales: 0,
      probability: 0,
      year: selectedYear,
      status: "Included",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: null,
      isNew: true,
      isDirty: true,
    };
    setEditableRows(prev => [...prev, newRow]);
  }, [clientId, selectedYear]);

  // Update local row data
  const handleRowChange = useCallback((index: number, field: keyof EditableRow, value: string | number) => {
    setEditableRows(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
        isDirty: true,
      };
      return updated;
    });
  }, []);

  // Save row
  const handleSaveRow = useCallback(async (row: EditableRow, index: number) => {
    if (row.isNew) {
      await createMutation.mutateAsync({
        client_id: clientId,
        description: row.description || "",
        sales: row.sales,
        probability: row.probability,
        year: selectedYear,
      });
    } else {
      await updateMutation.mutateAsync({
        id: row.id,
        description: row.description || "",
        sales: row.sales,
        probability: row.probability,
      });
    }
    
    // Mark as not dirty after save
    setEditableRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isDirty: false, isNew: false };
      return updated;
    });
  }, [clientId, selectedYear, createMutation, updateMutation]);

  // Delete row
  const handleDeleteRow = useCallback(async (row: EditableRow, index: number) => {
    if (row.isNew) {
      // Just remove from local state
      setEditableRows(prev => prev.filter((_, i) => i !== index));
    } else {
      await deleteMutation.mutateAsync(row.id);
    }
  }, [deleteMutation]);

  // Calculate estimated sales
  const calculateEstimatedSales = (sales: number, probability: number): number => {
    return Math.round(sales * (probability / 100) * 100) / 100;
  };

  // Calculate totals
  const totals = editableRows.reduce(
    (acc, row) => ({
      sales: acc.sales + (row.sales || 0),
      estimatedSales: acc.estimatedSales + calculateEstimatedSales(row.sales || 0, row.probability || 0),
    }),
    { sales: 0, estimatedSales: 0 }
  );

  // Format currency
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("el-GR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" /> Client Exploitation
        </CardTitle>
        <Select
          value={selectedYear.toString()}
          onValueChange={(value) => setSelectedYear(parseInt(value))}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Select year" />
          </SelectTrigger>
          <SelectContent>
            {yearOptions.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-2">
            {/* Header row */}
            <div className="grid grid-cols-[1fr_120px_100px_120px_100px_80px] gap-2 text-xs font-medium text-muted-foreground border-b pb-2">
              <div>Description</div>
              <div className="text-right">Sales (€)</div>
              <div className="text-right">Probability (%)</div>
              <div className="text-right">Estimated Sales</div>
              <div>Status</div>
              <div className="text-center">Actions</div>
            </div>

            {/* Data rows */}
            {editableRows.map((row, index) => (
              <div
                key={row.id}
                className={cn(
                  "grid grid-cols-[1fr_120px_100px_120px_100px_80px] gap-2 items-center py-1",
                  row.isDirty && "bg-yellow-50 dark:bg-yellow-950/20 -mx-2 px-2 rounded"
                )}
              >
                <Input
                  value={row.description || ""}
                  onChange={(e) => handleRowChange(index, "description", e.target.value)}
                  placeholder="Enter description..."
                  className="h-8 text-sm"
                />
                <Input
                  type="number"
                  value={row.sales || ""}
                  onChange={(e) => handleRowChange(index, "sales", parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="h-8 text-sm text-right"
                  min={0}
                />
                <Input
                  type="number"
                  value={row.probability || ""}
                  onChange={(e) => {
                    const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                    handleRowChange(index, "probability", val);
                  }}
                  placeholder="0"
                  className="h-8 text-sm text-right"
                  min={0}
                  max={100}
                />
                <div className="text-sm text-right font-medium text-primary">
                  {formatCurrency(calculateEstimatedSales(row.sales || 0, row.probability || 0))}
                </div>
                <Select
                  value={row.status || ""}
                  onValueChange={(value) => handleRowChange(index, "status", value)}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPLOITATION_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center justify-center gap-1">
                  {row.isDirty && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={() => handleSaveRow(row, index)}
                      disabled={createMutation.isPending || updateMutation.isPending}
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => handleDeleteRow(row, index)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}

            {/* Empty state */}
            {editableRows.length === 0 && (
              <div className="text-center py-6 text-muted-foreground text-sm">
                No exploitation data for {selectedYear}. Click + to add a row.
              </div>
            )}

            {/* Totals row */}
            {editableRows.length > 0 && (
              <div className="grid grid-cols-[1fr_120px_100px_120px_100px_80px] gap-2 items-center pt-3 border-t font-medium">
                <div className="text-sm">Total</div>
                <div className="text-sm text-right">{formatCurrency(totals.sales)}</div>
                <div className="text-sm text-right text-muted-foreground">—</div>
                <div className="text-sm text-right text-primary">{formatCurrency(totals.estimatedSales)}</div>
                <div />
                <div />
              </div>
            )}

            {/* Add row button */}
            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full border-dashed"
                onClick={handleAddRow}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Row
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
